import { useTranslation } from 'react-i18next';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import useRequest from '../../../hooks/use-request.hook';
import { useEffect, useMemo, useState } from 'react';
import Header from '../../../components/layout/header.component';
import Datatable from '../../../components/datatable/datatable.component';
import { AlertService } from '../../../services/alert.service';
import Icon from '../../../components/forms/icon.component';
import DateRegister from '../../../components/datatable/register-data.component';
import { FlowsService } from '../../../services/flows.service';
import Footer from '../../../components/layout/footer.component';
import PrincipalCard from '../../../components/cards/principal-card.component';
import Asaid from '../../../components/layout/asaid.component';
import { useNavigate } from 'react-router-dom';
import ActionsDropdown from '../../../components/datatable/actions-dropdown.component';
import EditFlowModal from './modals/edit-flows.modal';
import AddFlowModal from './modals/add-flow.modal';

interface Account {
  _id?: string;
  name?: string;
  company?: {
    name?: string;
  };
}

interface Flow {
  _id: string;
  name: string;
  created_at: string;
  status: boolean;
  nodesCount?: number;
  account?: Account;
}

function FlowsPage() {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const [flows, setFlows] = useState<Flow[]>([]);
  const [totalFlows, setTotalFlows] = useState<number>(0);
  const [activeFlows, setActiveFlows] = useState<number>(0);
  const [showEditModal, setShowEditModal] = useState({
    show: false,
    data: null
  });
  const [showAddFlowModal, setShowAddFlowModal] = useState(false);
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        name: 'Nombre',
        sortable: true,
        width: 25,
        cell: (row: any) => (
          <li className="list-group-item">
            <div className="d-flex flex-column">
              <div className="mw-300px mb-1 text-truncate">
                <strong className="fs-6">{t('templates.wa.name')}:</strong>
                <span className="ms-1">{row.name || t('general.no-info')}</span>
              </div>
              <div className="w-100 mb-1">
                <strong className="fs-6">{t('blacklist-page.company-label')}:</strong>
                <span className="ms-1">{row.account?.company?.name ?? t('general.no-info')}</span>
              </div>
              <div className="w-100 mb-1 d-flex align-items-center">
                <strong className="fs-6">{t('blacklist-page.account-label')}:</strong>
                <span className="ms-1">{row.account?.name ?? t('general.no-info')}</span>
              </div>
            </div>
          </li>
        )
      },
      {
        name: 'Creado',
        sortable: true,
        width: 15,
        cell: (row: any) => <DateRegister created_at={row.created_at} />
      },
      {
        name: 'Estado',
        sortable: true,
        width: 10,
        cell: (row: any) => {
          const isActive = row.status;
          return (
            <span className="badge text-dark fs-6">
              {isActive ? (
                <>
                  <Icon icon="ki-check-circle" size="fs-1" type="primary" />
                  Activo
                </>
              ) : (
                <>
                  <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
                  Inactivo
                </>
              )}
            </span>
          );
        }
      },
      {
        name: 'Acciones',
        sortable: false,
        center: true,
        exportable: false,
        width: 20,
        cell: (rows: any) => {
          const options = [
            {
              label: 'Configura tu flujo',
              icon: 'ki-setting-2',
              color: 'warning',
              onClick: () =>
                navigate('/sendCampaigns/flows-answers/builder', {
                  state: {
                    _id: rows._id,
                    name: rows.name,
                    account: rows.account?.name ?? '',
                    accountId: rows.account?._id ?? ''
                  }
                })
            }
          ];
          return (
            <ActionsDropdown
              options={options}
              rowStatus={rows.status}
              edit={() => setShowEditModal({ show: true, data: rows })}
              changeStatus={() => handleChangeStatusFlow(rows._id, rows.status)}
            />
          );
        }
      }
    ],
    [t, navigate]
  );

  const handleShowEditModal = async (refresh: boolean) => {
    if (refresh) {
      await getFlows();
    }
    setShowEditModal({ show: false, data: null });
  };
  const handleShowAddFlowModal = async (refresh: boolean) => {
    if (refresh) {
      await getFlows();
    }

    setShowAddFlowModal(false);
  };
  const handleFlowCreated = async (newFlow: { _id: string; name: string; account: string }) => {
    navigate('/sendCampaigns/flows-answers/builder', {
      state: {
        _id: newFlow._id,
        name: newFlow.name,
        accountId: newFlow.account
      }
    });
  };

  const handleChangeStatusFlow = async (id: string, status: boolean) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      '¿Estás seguro que deseas desactivar el flujo?',
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await FlowsService.changeStatusFlow({ bot_id: id, status: !status });
      AlertService.toastSuccess('Se ha actualizado el estado del flujo');
      await getFlows();
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getFlows = async () => {
    try {
      const data = await call(() => FlowsService.getFlows());
      setFlows(data);
      setTotalFlows(data.length);
      setActiveFlows(data.filter((f: Flow) => f.status).length);
    } catch (err: any) {
      setFlows([]);
      console.log('Error', err);
      AlertService.toastError(err.error || err);
    }
  };

  useEffect(() => {
    getFlows();
  }, []);

  const stats = useMemo(
    () => [
      { label: 'Activos', total: activeFlows, icon: 'ki-check-circle', type: 'info' },
      {
        label: 'Total',
        total: totalFlows,
        icon: 'ki-check-circle',
        type: 'primary'
      }
    ],
    [t, activeFlows, totalFlows]
  );

  const onAddFlow = async () => {
    setShowAddFlowModal(true);
  };

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={stats}
                  btnCreate
                  btnRefresh
                  onRefresh={async () => {
                    await getFlows();
                    AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                  }}
                  btnBack
                  returnExtrict="/sendCampaigns"
                  onClick={onAddFlow}
                  title="Flujos"
                  titleBolder="Campañas"
                  subtitle="Crea tus flujos de conversación"
                  breadcrumbItems={['Campañas']}
                  textButton="Crear flujo"
                  iconButton="ki-soft-2"
                />
                <div className="row g-xl-12">
                  <div className="col-xl-12 mb-5">
                    <div className="card mb-5 mb-xxl-8 mt-0">
                      <div className="card-header align-items-center border-0 mt-5">
                        <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                          <span className="fw-bolder fw-bold fs-2">Flujos</span>
                          <span className="text-gray-700 mt-2 fw-semibold fs-6">
                            Visualiza y gestiona tus flujos
                          </span>
                        </div>
                      </div>
                      <div className="card-body pt-1">
                        <Datatable columns={columns} data={flows} isLoading={isRequesting} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      <AddFlowModal
        show={showAddFlowModal}
        onRequestClose={(refresh: any) => handleShowAddFlowModal(refresh)}
        onFlowCreated={(newFlow: any) => handleFlowCreated(newFlow)}
      />
      <EditFlowModal
        show={showEditModal.show}
        onRequestClose={(refresh: any) => handleShowEditModal(refresh)}
        payload={showEditModal.data}
      />
    </>
  );
}

export default FlowsPage;
